import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { X, ArrowRight, Wallet, RefreshCw, Sparkles } from "lucide-react";
import { useApp } from "../context/AppContext";

const STEPS = [
    {
        icon: Wallet,
        gradient: "from-[#7DD3B0] via-[#4FD4C7] to-[#7EE8DE]",
        title_es: "Conexión segura",
        title_en: "Secure Connection",
        desc_es: "Conecta tus cuentas de forma segura para ver tu panorama financiero en un solo lugar.",
        desc_en: "Connect your accounts securely to see your financial picture in one place.",
        cta_es: "Conectar mi banco",
        cta_en: "Connect My Bank",
        route: "/dashboard",
    },
    {
        icon: RefreshCw,
        gradient: "from-[#F9AE7C] via-[#FFAF3D] to-[#F9C77E]",
        title_es: "Seguimiento inteligente",
        title_en: "Smart Tracking",
        desc_es: "Categorizamos automáticamente tus gastos diarios para que veas exactamente a dónde va tu dinero.",
        desc_en: "Automatically categorize your daily spending to see exactly where your money goes.",
        cta_es: "Ver mi panel",
        cta_en: "See my dashboard",
        route: "/dashboard",
    },
    {
        icon: Sparkles,
        gradient: "from-[#B85A3E] via-[#C6702E] to-[#E39051]",
        title_es: "Crecimiento financiero",
        title_en: "Financial Growth",
        desc_es: "Recibe consejos personalizados y alcanza tus metas de ahorro más rápido con tu propio asistente con IA.",
        desc_en: "Get personalized advice and reach your savings goals faster with your own money coach.",
        cta_es: "Habla con Caesar",
        cta_en: "Talk to Caesar",
        route: null, // opens widget
    },
];

const ExplainerCards = ({ open, onClose }) => {
    const { language } = useApp();
    const navigate = useNavigate();
    const [idx, setIdx] = useState(0);

    React.useEffect(() => {
        if (open) setIdx(0);
    }, [open]);

    if (!open) return null;
    const step = STEPS[idx];
    const Icon = step.icon;
    const isLast = idx === STEPS.length - 1;

    const handleCta = () => {
        onClose();
        if (step.route) {
            setTimeout(() => navigate(step.route), 250);
        } else {
            // trigger Caesar widget open via URL hash
            setTimeout(() => {
                const btn = document.querySelector("[data-testid='caesar-widget-open']");
                if (btn) btn.click();
            }, 250);
        }
    };

    const next = () => {
        if (isLast) {
            handleCta();
        } else {
            setIdx((i) => Math.min(STEPS.length - 1, i + 1));
        }
    };
    const prev = () => setIdx((i) => Math.max(0, i - 1));

    return (
        <AnimatePresence>
            <motion.div
                key="backdrop"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
                data-testid="explainer-backdrop"
                onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
            >
                <motion.div
                    key={`card-${idx}`}
                    initial={{ opacity: 0, y: 20, scale: 0.96 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.98 }}
                    transition={{ duration: 0.3, ease: "easeOut" }}
                    className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden text-neutral-900"
                    data-testid="explainer-card"
                >
                    {/* Gradient header with icon */}
                    <div className={`relative h-32 bg-gradient-to-br ${step.gradient}`}>
                        <button
                            onClick={onClose}
                            data-testid="explainer-close"
                            className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/25 hover:bg-white/40 backdrop-blur grid place-items-center text-white transition-colors"
                            aria-label="Close"
                        >
                            <X className="w-4 h-4" />
                        </button>
                        <div className="absolute left-1/2 -bottom-6 -translate-x-1/2 w-14 h-14 rounded-2xl bg-white shadow-lg grid place-items-center border border-neutral-200">
                            <Icon className="w-6 h-6 text-neutral-900" />
                        </div>
                    </div>

                    <div className="pt-10 pb-6 px-6">
                        {/* Progress + counter */}
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-1.5" data-testid="explainer-progress">
                                {STEPS.map((_, i) => (
                                    <span
                                        key={i}
                                        className={`h-1 rounded-full transition-all ${
                                            i === idx ? "w-8 bg-neutral-900" : "w-4 bg-neutral-200"
                                        }`}
                                    />
                                ))}
                            </div>
                            <div className="text-xs text-neutral-500 font-medium tabular-nums">
                                {idx + 1} {language === "es" ? "de" : "of"} {STEPS.length}
                            </div>
                        </div>

                        <h3 className="font-display font-extrabold text-xl md:text-2xl tracking-tight text-neutral-900">
                            {language === "es" ? step.title_es : step.title_en}
                        </h3>
                        <p className="mt-2 text-sm md:text-base text-neutral-600 leading-relaxed">
                            {language === "es" ? step.desc_es : step.desc_en}
                        </p>

                        <div className="mt-6 flex items-center justify-between gap-3">
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={prev}
                                    disabled={idx === 0}
                                    data-testid="explainer-prev"
                                    className="text-sm font-medium text-neutral-500 hover:text-neutral-900 disabled:opacity-30 disabled:cursor-not-allowed px-2 py-1"
                                >
                                    {language === "es" ? "Anterior" : "Previous"}
                                </button>
                            </div>
                            <div className="flex items-center gap-3">
                                <button
                                    onClick={handleCta}
                                    data-testid="explainer-cta"
                                    className="pill inline-flex items-center gap-2 px-4 py-2 bg-neutral-900 text-white text-sm font-semibold hover:bg-neutral-800 transition-colors"
                                >
                                    <ArrowRight className="w-4 h-4" />
                                    {language === "es" ? step.cta_es : step.cta_en}
                                </button>
                                <button
                                    onClick={next}
                                    disabled={isLast}
                                    data-testid="explainer-next"
                                    className="text-sm font-medium text-neutral-500 hover:text-neutral-900 disabled:opacity-30 disabled:cursor-not-allowed px-2 py-1"
                                >
                                    {isLast ? (language === "es" ? "Listo" : "Done") : (language === "es" ? "Siguiente" : "Next")}
                                </button>
                            </div>
                        </div>
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
};

export default ExplainerCards;
